import React, { useRef, useEffect, useCallback } from 'react'
import { Chart, Tooltip } from 'chart.js'
import { useTheme } from '@iqueue/ui-kit'
import moment from 'moment'
import { t } from '../../../hooks/useTranslate'

function fillPath(ctx, color, { x, y }, ...points) {
  ctx.save()
  ctx.beginPath()
  ctx.moveTo(x, y)

  points.forEach(({ x, y }) => ctx.lineTo(x, y))

  ctx.fillStyle = color
  ctx.closePath()
  ctx.fill()

  ctx.restore()
}

function findIntersection(from1, to1, from2, to2) {
  const dX = to1.x - from1.x
  const dY = to1.y - from1.y

  const d = dX * (to2.y - from2.y) - (to2.x - from2.x) * dY
  if (d === 0) return undefined // parallel lines

  const lambda = ((to2.y - from2.y) * (to2.x - from1.x) + (from2.x - to2.x) * (to2.y - from1.y)) / d
  const gamma = ((from1.y - to1.y) * (to2.x - from1.x) + dX * (to2.y - from1.y)) / d

  if (!(0 <= lambda && lambda <= 1) || !(0 <= gamma && gamma <= 1)) return undefined // no intersection

  return {
    x: from1.x + lambda * dX,
    y: from1.y + lambda * dY,
  }
}

Tooltip.positioners.myCustomPositioner = function (elements, eventPosition) {
  return {
    x: 0,
    y: 0,
  }
}

const fillBetweenLinesPlugin = {
  id: 'fillBetweenLinesPlugin',
  afterDatasetsDraw: function (chart) {
    const ctx = chart.ctx
    ctx.save()

    const plume = chart.getDatasetMeta(0)
    const well = chart.getDatasetMeta(1)

    const goodColor = 'rgba(88,172,48,0.4)'
    const badColor = 'rgba(244,67,54,0.4)'

    if (!plume.visible || !well.visible) {
      return
    }

    let pathColor = goodColor
    let topPoints = []
    let bottomPoints = []
    for (let i = 0; i < plume.data.length - 1; i++) {
      topPoints.push(plume.data[i])
      bottomPoints.unshift(well.data[i])

      const intersection = findIntersection(
        plume.data[i],
        plume.data[i + 1],
        well.data[i],
        well.data[i + 1],
      )

      if (intersection) {
        if (plume.data[i].y > well.data[i].y) {
          pathColor = goodColor
        } else {
          pathColor = badColor
        }

        topPoints.push(intersection)

        ctx.globalCompositeOperation = 'destination-over'
        fillPath(ctx, pathColor, ...topPoints, ...bottomPoints)
        topPoints = [intersection]
        bottomPoints = []

        pathColor = pathColor === goodColor ? badColor : goodColor
      }
    }

    topPoints.push(plume.data[plume.data.length - 1])
    bottomPoints.unshift(well.data[well.data.length - 1])
    fillPath(ctx, pathColor, ...topPoints, ...bottomPoints)
    ctx.restore()
  },
}

export default function WellGraph({ summary, chartData }) {
  const pressurePlumeData = summary?.PRESSURE_PLUME?.chart || []
  const pressureWellData = summary?.PRESSURE_WELL?.chart || []
  const temperatureData = summary?.TEMPERATURE?.chart || []
  const { getCSSValue } = useTheme()

  // const {
  //   pressurePlumeData, pressureWellData, temperatureData
  // } = summary

  const chartRef = useRef()

  const getTemperatureStyle = useCallback((ctx, temp) => {
    let color

    function mixin(ctx, c1, c2) {
      const mix = ctx.createLinearGradient(0, 0, 0, 300)
      mix.addColorStop(0, c1)
      mix.addColorStop(1, c2)

      return mix
    }

    if (temp < 35) {
      // color = mixin(ctx, 'rgba(4,138,26,1)', 'rgba(4,138,26,0.3)')
      color = 'rgba(4,138,26,1)'
    } else if (temp >= 35 && temp < 40) {
      // color = mixin(ctx, 'rgba(253, 187, 45, 1)', 'rgba(253, 187, 45, 0.3)')
      color = 'rgba(253, 187, 45, 1)'
    } else if (temp >= 40 && temp < 60) {
      // color = mixin(ctx, 'rgba(255, 87, 51, 1)', 'rgba(255, 87, 51, 0.3)')
      color = 'rgba(255, 87, 51, 1)'
    } else {
      // color = mixin(ctx, 'rgba(255, 0, 0, 1)', 'rgba(255, 0, 0, 0.3)')
      color = 'rgba(255, 0, 0, 1)'
    }

    return {
      backgroundColor: color,
    }
  }, [])

  useEffect(() => {
    const ctx = chartRef.current.getContext('2d')
    const temperatureColors = Object.values(temperatureData).map(temp => getTemperatureStyle(ctx, temp).backgroundColor)
    const labels = Object.keys(pressurePlumeData).map(timestamp => moment.utc(timestamp).format('YYYY-MM-DD HH:mm'))

    const chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: t('chart.pressurePlume'),
            data: Object.values(pressurePlumeData),
            // borderColor: 'rgba(0, 188, 212, 0.99)',
            borderColor: getCSSValue('--iq-color'),
            fill: false,
            type: 'line',
            tension: 0,
            pointRadius: 0,
            borderWidth: 2,
            fillBetweenSet: 1,
            order: 1,
          },
          {
            label: t('chart.pressureWell'),
            data: Object.values(pressureWellData),
            // borderColor: 'rgba(0,60,255,1)',
            borderColor: getCSSValue('--iq-accent-secondary'),
            fill: false,
            type: 'line',
            pointRadius: 0,
            borderWidth: 2,
            order: 2,
          },
          {
            label: t('chart.temperature'),
            data: Object.values(temperatureData),
            // borderColor: 'rgba(248,247,247,0.6)',
            backgroundColor: temperatureColors,
            fill: true,
            type: 'bar',
            barThickness: 3,
            pointRadius: 0,
            order: 0,
          },
        ],
      },
      options: {
        interaction: {
          intersect: false,
          mode: 'index',
        },
        scales: {
          x: {
            type: 'category',
            title: {
              display: true,
              text: t('chart.time'),
            },
          },
          y: {
            title: {
              display: true,
              text: t('chart.pressureUnit'),
            },
            position: 'left',
          },
          temperature: {
            type: 'linear',
            position: 'right',
            title: {
              display: true,
              text: t('chart.temperatureUnit'),
            },
            min: 0,
            max: 100,
          },
        },
      },
      plugins: [fillBetweenLinesPlugin],
    })

    return () => {
      chart.destroy()
    }
  }, [pressurePlumeData, pressureWellData, temperatureData])

  return <div className={ 'chart-left' }>
    <canvas ref={ chartRef }/>
  </div>
}
